// import React, { useMemo, useCallback } from "react";
import React, { useMemo, useCallback, useEffect } from "react";
import { Container, Text } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { formatToCustomLocale } from "../../../../utilities";
import { setToolbarEdge } from "./toolbarEdges";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../pixiSettings";
import { outlineShadow } from "../../../../pixiSettings";
import {
  selectBet,
  selectBets,
  selectCurrency,
  selectBetSelectPopupActive,
  selectCriticalAnimationPlaying,
  selectIsSpinning,
  selectAutoPlayActive,
  selectAnyFeatureTriggeredOrActive,
  selectFreeRoundsActive,
  selectReplaySessionActive,
  selectReplayBet,
} from "../../../../State/selectors";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { openBetSelectPopup, closeBetSelectPopup } from "../../../../State/actions";
import ButtonDecrementBet from "../Buttons/ButtonDecrementBet";
import ButtonIncrementBet from "../Buttons/ButtonIncrementBet";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

const BetDisplay = ({ toolbar, position }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const bet = useSelector(selectBet);
  const bets = useSelector(selectBets);
  const currency = useSelector(selectCurrency);
  const popupActive = useSelector(selectBetSelectPopupActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const isSpinning = useSelector(selectIsSpinning);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const anyFeatureActive = useSelector(selectAnyFeatureTriggeredOrActive);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const replaySessionActive = useSelector(selectReplaySessionActive);
  const replayBet = useSelector(selectReplayBet);
  const { device, orientation, getResponsiveConfig, uiSymbolScale, layout } =
    useDimensions();

  // While a replay round is active, show its own recorded bet instead of the
  // live bet (docs/replay-history-spec.md §9) — state.game.bet itself is
  // never touched by replay, so this falls back to the real value the
  // instant sessionActive goes false again, no restore step needed.
  const effectiveBet =
    replaySessionActive && replayBet != null ? replayBet : bet;

  // Get button dimensions using the same system as PopiBalanceDisplay - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Get unified bet configuration (font multiplier, y position) - memoized
  // X position now comes from toolbar.layout
  const betConfig = useMemo(() => {
    let fontMultiplier = 1.0;
    let y = 0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        // fontMultiplier = 1.25;
        // A tiny bit larger on an iPad upright (layout TABLET_PORTRAIT on
        // the MOBILE device, 2026-10-29).
        // fontMultiplier = layout === "TABLET_PORTRAIT" ? 1.3 : 1.25;
        // fontMultiplier = layout === "TABLET_PORTRAIT" ? 1.4 : 1.25;
        // fontMultiplier = layout === "TABLET_PORTRAIT" ? 1.45 : 1.25;
        fontMultiplier = layout === "TABLET_PORTRAIT" ? 1.4 : 1.25;
        y = -toolbar.visualHeight / 2;
      } else {
        fontMultiplier = 1.1;
        y = toolbar.height / 2;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.5;
        y = -toolbar.visualHeight / 2;
      } else {
        fontMultiplier = 1.25;
        y = toolbar.height / 2;
      }
    } else {
      // Desktop
      fontMultiplier = 1.0;
      y = toolbar.height / 2;
    }

    return { fontMultiplier, y };
  }, [
    device,
    orientation,
    layout,
    toolbar.height,
    toolbar.visualHeight,
  ]);

  // Calculate font sizes - memoized
  const baseFontSize = 32;
  const FONT_SIZE = useMemo(() => {
    const responsiveFontSize = baseFontSize * betConfig.fontMultiplier;
    const raw = responsiveFontSize * uiSymbolScale;
    return Math.round(raw / 2) * 2; // Snap to even (matches BalanceDisplay)
  }, [betConfig.fontMultiplier, uiSymbolScale]);

  const LABEL_FONT_SIZE = useMemo(
    () => Math.round((FONT_SIZE * 0.9) / 2) * 2, // Snap to even (matches BalanceDisplay)
    [FONT_SIZE]
  );

  const dpr = window.devicePixelRatio || 1;
  const betLabelStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: LABEL_FONT_SIZE,
        fontWeight: "500",
        fill: 0xffffff,
        align: "left",
        resolution: dpr,
        // ...TEXT_STYLE_CONFIG.OUTLINE,
        ...outlineShadow(LABEL_FONT_SIZE), // shadow scales with the text
      }),
    [LABEL_FONT_SIZE, dpr]
  );

  // Format bet and prepare text content - memoized
  const formattedBet = useMemo(
    () => formatToCustomLocale(effectiveBet),
    [effectiveBet],
  );
  const betLabelText = useMemo(() => t("total_bet"), [t]);
  const betAmountText = useMemo(
    () => `${formattedBet} ${currency}`,
    [formattedBet, currency]
  );

  // Calculate max bet width based on largest bet in bets array (keeps buttons fixed)
  const maxBetWidth = useMemo(() => {
    if (!bets || bets.length === 0) {
      // Fallback: measure current bet if bets array not available
      const tempStyle = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: FONT_SIZE,
        fontWeight: "700",
      });
      return TextMetrics.measureText(betAmountText, tempStyle).width;
    }

    const maxBet = Math.max(...bets);
    const maxBetText = `${formatToCustomLocale(maxBet)} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: FONT_SIZE,
      fontWeight: "700",
    });
    const maxBetMetrics = TextMetrics.measureText(maxBetText, tempStyle);

    return maxBetMetrics.width;
  }, [bets, currency, FONT_SIZE, betAmountText]);

  // Auto-scale font size if actual bet text exceeds max width (matches BalanceDisplay)
  const finalBetFontSize = useMemo(() => {
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: FONT_SIZE,
      fontWeight: "700",
    });
    const metrics = TextMetrics.measureText(betAmountText, tempStyle);

    // If text exceeds max width, scale down proportionally
    if (metrics.width > maxBetWidth) {
      const scale = maxBetWidth / metrics.width;
      return Math.round(FONT_SIZE * scale);
    }

    // Return ideal font size if it fits
    return FONT_SIZE;
  }, [betAmountText, FONT_SIZE, maxBetWidth]);

  const betAmountStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalBetFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "left",
        resolution: dpr,
        // ...TEXT_STYLE_CONFIG.OUTLINE,
        ...outlineShadow(finalBetFontSize), // shadow scales with the text
      }),
    [finalBetFontSize, dpr]
  );

  // Measure text dimensions for positioning - memoized
  const textMetrics = useMemo(() => {
    const labelMetrics = TextMetrics.measureText(betLabelText, betLabelStyle);
    const amountMetrics = TextMetrics.measureText(betAmountText, betAmountStyle);

    const totalHeight = (labelMetrics.height + amountMetrics.height) * 0.95; // Reduce gap by 5% (matches BalanceDisplay)
    const maxWidth = Math.max(labelMetrics.width, maxBetWidth);

    const labelY = Math.round(-totalHeight / 2 + labelMetrics.height / 2);
    const amountY = Math.round(totalHeight / 2 - amountMetrics.height / 2);

    return {
      labelY,
      amountY,
      maxWidth,
      drawnWidth: Math.max(labelMetrics.width, amountMetrics.width),
    };
  }, [betLabelText, betAmountText, betLabelStyle, betAmountStyle, maxBetWidth]);

  // Button positioning - memoized
  const buttonPositions = useMemo(() => {
    const gap = buttonConfig.width * 0.3;

    // Calculate spacing: button + gap + text + gap + button
    const totalControlsWidth =
      buttonConfig.width + gap + textMetrics.maxWidth + gap + buttonConfig.width;

    // Position everything symmetrically around center
    const decrementX = -totalControlsWidth / 2;
    const textCenterX = Math.round(
      -totalControlsWidth / 2 + buttonConfig.width + gap + textMetrics.maxWidth / 2
    );
    const incrementX = totalControlsWidth / 2 - buttonConfig.width;

    return { decrementX, textCenterX, incrementX };
  }, [buttonConfig.width, textMetrics.maxWidth]);

  // Container positioning - memoized
  const containerPosition = useMemo(() => {
    // Use layout position from toolbar hook if available, with fallback to old logic
    const fallbackX =
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? toolbar.width - buttonConfig.width * 1.25
        : device === PixiSettings.DEVICE.TABLET &&
          orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? toolbar.width - buttonConfig.width * 2
        : device === PixiSettings.DEVICE.TABLET &&
          orientation === PixiSettings.ORIENTATION.LANDSCAPE
        ? buttonConfig.width * 9.0
        : buttonConfig.width * 8.5;

    const containerX = position?.x ?? toolbar.layout?.bet?.x ?? fallbackX;
    const containerY = position?.y ?? betConfig.y;
    const buttonsY = 0;

    return { containerX, containerY, buttonsY };
  }, [
    position?.x,
    position?.y,
    toolbar.layout?.bet?.x,
    toolbar.width,
    device,
    orientation,
    buttonConfig.width,
    betConfig.y,
  ]);

  // Desktop flags - memoized
  const showButtons = useMemo(
    () => device === PixiSettings.DEVICE.DESKTOP,
    [device]
  );
  const isDesktop = useMemo(
    () => device === PixiSettings.DEVICE.DESKTOP,
    [device]
  );

  // Right-align text on mobile/tablet portrait
  const isPortraitMobile =
    (device === PixiSettings.DEVICE.MOBILE || device === PixiSettings.DEVICE.TABLET) &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  // Its text's left edge, for the win in the middle (toolbarEdges).
  useEffect(() => {
    setToolbarEdge(
      "betLeft",
      isPortraitMobile ? containerPosition.containerX - textMetrics.drawnWidth : null,
    );
  }, [isPortraitMobile, containerPosition.containerX, textMetrics.drawnWidth]);
  useEffect(() => () => setToolbarEdge("betLeft", null), []);

  // Calculate if bet selection should be disabled
  const isDisabled =
    criticalAnimationPlaying ||
    anyFeatureActive ||
    freeRoundsActive ||
    isSpinning ||
    autoPlayActive ||
    replaySessionActive;

  // Handle bet display click (desktop only - toggles bet select popup)
  const handleBetDisplayClick = useCallback(() => {
    // Don't allow bet selection during critical states
    if (isDisabled) {
      return;
    }

    if (isDesktop) {
      soundManager.play(SOUND_NAME.button_click);
      if (popupActive) {
        dispatch(closeBetSelectPopup());
      } else {
        dispatch(openBetSelectPopup());
      }
    }
  }, [isDesktop, isDisabled, popupActive, dispatch]);

  return (
    <Container
      name="secondary_bet_display"
      x={containerPosition.containerX}
      y={containerPosition.containerY}
      zIndex={5}
    >
      {/* Clickable text container (desktop only) */}
      <Container
        name="bet_text_container"
        eventMode={isDesktop && !isDisabled ? "static" : "none"}
        cursor={isDesktop && !isDisabled ? "pointer" : "default"}
        onpointertap={handleBetDisplayClick}
      >
        <Text
          text={betLabelText}
          style={betLabelStyle}
          anchor={{ x: isPortraitMobile ? 1 : 0.5, y: 0.5 }}
          x={isPortraitMobile ? 0 : buttonPositions.textCenterX}
          y={textMetrics.labelY}
          roundPixels={true}
        />
        <Text
          text={betAmountText}
          style={betAmountStyle}
          anchor={{ x: isPortraitMobile ? 1 : 0.5, y: 0.5 }}
          x={isPortraitMobile ? 0 : buttonPositions.textCenterX}
          y={textMetrics.amountY}
          roundPixels={true}
        />
      </Container>

      {showButtons && (
        <ButtonDecrementBet
          toolbar={toolbar}
          x={buttonPositions.decrementX}
          y={containerPosition.buttonsY}
        />
      )}
      {showButtons && (
        <ButtonIncrementBet
          toolbar={toolbar}
          x={buttonPositions.incrementX}
          y={containerPosition.buttonsY}
        />
      )}
    </Container>
  );
};

export default BetDisplay;
